/**
 * Quick-study open-end reads drawn through the shared plots (wave 7, Q12, Q13, Q14, Q16).
 *
 * New insights carry Python's stamps (`theme_plot`, `monadic_theme_plot`, `quote_plot`, and
 * the theme heat grids `monadic_theme_heat_plot` / `theme_splits_plot`, W7-T4b);
 * these hosts draw them with the plot library and add only what a host owns: the
 * viewer-language words of the "Other" link, the portraits and speaker names Python could
 * not stamp (filled by `respondentId` from the run's personas; portraits then from the slim
 * picker view), comparison-name
 * bolding, and the segment cut (stamped quotes kept by `respondentId`, shape only).
 * Stored results carry no stamp: `read*` returns null and the cards keep their legacy
 * readers. Leaf module: imports no card, so the cards import it without a cycle.
 */
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ChoiceBarsPlot } from '@digital-twins/deck-ui/plots/choice-bars'
import { CategoricalSharePlotView } from '@digital-twins/deck-ui/plots/categorical-share'
import { HeatGridPlot } from '@digital-twins/deck-ui/plots/heat-grid'
import { QuoteGallery, QuoteGalleryCard, type QuoteGalleryAvatarArgs } from '@digital-twins/deck-ui/plots/quote-list'

import { UserAvatar } from '@/components/ui/UserAvatar'
// Deep import (not the study-doc barrel), as QuickStudyOpenEndCard does: the slim picker
// fetch the portrait mosaic band uses to fill a missing image.
import { resolveMosaicPickerImages } from '@/components/study-doc/PortraitMosaicStrip/resolveMosaicPickerImages'
import { cn } from '@/lib/utils'
import { personaThumbUrl } from '@/utils/personaThumbUrl'
import { ComparisonLabelText } from './ComparisonLabelText'

import type { StoredPlotPayload } from '@digital-twins/deck-ui/plots/contract'
import type { QuoteListQuote } from '@digital-twins/slide-schemas/generated/plot-types'
import type { QuickStudyPersona } from './QuickStudyTwinsPanel'

export type ThemePlot = StoredPlotPayload<'choice-bars'>
export type MonadicThemePlot = StoredPlotPayload<'grouped-bars'>
export type QuotePlot = StoredPlotPayload<'quote-list'>
export type ThemeHeatPlot = StoredPlotPayload<'heat-grid'>

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** A stamped pooled-themes plot, or null (stored results: the legacy ThemesView draws them). */
export function readThemePlot(value: unknown): ThemePlot | null {
  return isRecord(value) && value.kind === 'choice-bars' && isRecord(value.data) && Array.isArray(value.data.series)
    ? (value as unknown as ThemePlot)
    : null
}

/** A stamped monadic codeframe plot, or null (stored results: GroupedThemeBar draws them). */
export function readMonadicThemePlot(value: unknown): MonadicThemePlot | null {
  return isRecord(value) && value.kind === 'grouped-bars' && isRecord(value.data) && Array.isArray(value.data.rows)
    ? (value as unknown as MonadicThemePlot)
    : null
}

/** A stamped quote gallery, or null (stored results: the browser-built gallery draws them). */
export function readQuotePlot(value: unknown): QuotePlot | null {
  return isRecord(value) && value.kind === 'quote-list' && isRecord(value.data) && Array.isArray(value.data.quotes)
    ? (value as unknown as QuotePlot)
    : null
}

/** A stamped theme heat grid (W7-T4b), or null (stored results: `MonadicThemeHeatmap` draws them). */
export function readThemeHeatPlot(value: unknown): ThemeHeatPlot | null {
  return isRecord(value) &&
    value.kind === 'heat-grid' &&
    isRecord(value.data) &&
    Array.isArray(value.data.rows) &&
    Array.isArray(value.data.columns)
    ? (value as unknown as ThemeHeatPlot)
    : null
}

/** The "Other" link's words in the viewer's UI language (decision 23), as the choice card passes them. */
function useOtherToggleLabels() {
  const { t } = useTranslation('app')

  return {
    expand: (count: number) => t('studyReport.plotOther.expand', { count }),
    collapse: t('studyReport.plotOther.collapse'),
  }
}

export interface OpenEndThemesPlotProps {
  plot: ThemePlot
  /** A document is a static read: no "Other" link. */
  documentMode?: boolean
}

/** The pooled themes (Q12): choice-bars in sentiment sections, Other opening in place. */
export function OpenEndThemesPlot(props: OpenEndThemesPlotProps) {
  const { plot, documentMode = false } = props

  const { i18n } = useTranslation('app')
  const otherToggleLabels = useOtherToggleLabels()

  return (
    <div className="px-1 pt-1">
      <ChoiceBarsPlot
        data={plot.data}
        size="card"
        renderMode={documentMode ? 'static' : 'interactive'}
        locale={i18n.language}
        testIdPrefix="qs-open-end-theme-plot"
        otherToggleLabels={otherToggleLabels}
      />
    </div>
  )
}

/** A version's stamped themes (no shared codeframe), with the host's version name and colour key. */
export interface PerVersionThemePlot {
  id: string
  label: string
  plot: ThemePlot
}

/** Every version that carries a stamped theme plot, in stamped order (stored results: none). */
export function readPerVersionThemePlots(perItem: Array<{ creative_id: string; label: string; theme_plot?: unknown }>): Array<PerVersionThemePlot> {
  return perItem.flatMap((item) => {
    const plot = readThemePlot(item.theme_plot)
    return plot ? [{ id: item.creative_id, label: item.label, plot }] : []
  })
}

export interface PerVersionThemesPlotProps {
  versions: Array<PerVersionThemePlot>
  variantColor: Record<string, string>
  comparisonLabels?: Array<string>
  documentMode?: boolean
}

/**
 * A monadic run with no shared codeframe (wave 7 f1): each version's own themes as
 * small multiples, never on one axis (the themes were coded separately per version).
 */
export function PerVersionThemesPlot(props: PerVersionThemesPlotProps) {
  const { versions, variantColor, comparisonLabels = [], documentMode = false } = props

  const { t, i18n } = useTranslation('app')
  const otherToggleLabels = useOtherToggleLabels()

  return (
    <div className="space-y-3 px-1 pt-1" data-testid="qs-monadic-per-version-plots">
      {versions.length > 1 ? (
        <p className="text-xs text-twin-chat-fg-muted" data-testid="qs-monadic-per-version-note">
          {t('quickStudyChrome.themesPerVersionNote')}
        </p>
      ) : null}
      <div className={cn('grid grid-cols-1 gap-5', versions.length > 1 && 'sm:grid-cols-2')}>
        {versions.map((version) => (
          <div key={version.id} className="min-w-0 space-y-2" data-testid="qs-monadic-per-version-plot" data-version-id={version.id}>
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: variantColor[version.id] }} />
              <span className="text-xs font-medium text-twin-chat-fg-muted">
                <ComparisonLabelText text={version.label} labels={comparisonLabels} />
              </span>
            </div>
            <ChoiceBarsPlot
              data={version.plot.data}
              size="card"
              renderMode={documentMode ? 'static' : 'interactive'}
              locale={i18n.language}
              testIdPrefix={`qs-monadic-per-version-plot-${version.id}`}
              otherToggleLabels={otherToggleLabels}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

export interface MonadicThemesPlotProps {
  plot: MonadicThemePlot
  documentMode?: boolean
}

/** The monadic shared codeframe (Q13): grouped bars drawn a group per theme (`groupedBy: row`), a bar per version. */
export function MonadicThemesPlot(props: MonadicThemesPlotProps) {
  const { plot, documentMode = false } = props

  const { i18n } = useTranslation('app')
  const otherToggleLabels = useOtherToggleLabels()

  return (
    <div className="px-1 pt-1" data-testid="qs-monadic-theme-plot">
      <CategoricalSharePlotView
        plot={plot}
        size="card"
        locale={i18n.language}
        renderMode={documentMode ? 'static' : 'interactive'}
        testIdPrefix="qs-monadic-theme-plot-bars"
        otherToggleLabels={otherToggleLabels}
      />
    </div>
  )
}

export interface ThemeHeatGridPlotProps {
  plot: ThemeHeatPlot
  documentMode?: boolean
  testIdPrefix?: string
}

/**
 * The theme heatmap (Q14, W7-T4b): themes down in sentiment sections, versions or score bands
 * across, as Python stamped them (shares, heat, the Other row, the too-few-to-theme note).
 */
export function ThemeHeatGridPlot(props: ThemeHeatGridPlotProps) {
  const { plot, documentMode = false, testIdPrefix = 'qs-theme-heat-grid' } = props

  const { i18n } = useTranslation('app')

  return (
    <div className="px-1 pt-1" data-testid={testIdPrefix}>
      <HeatGridPlot
        data={plot.data}
        props={{ locale: i18n.language, renderMode: documentMode ? 'static' : 'interactive' }}
        testIdPrefix={`${testIdPrefix}-plot`}
      />
    </div>
  )
}

/** The portrait with the 128px thumbnail first, then the original, then the stamped initials. */
function GalleryPortrait(props: QuoteGalleryAvatarArgs) {
  const { quote, name, size } = props

  const [broken, setBroken] = useState(false)
  const [thumbFailed, setThumbFailed] = useState(false)

  const image = quote.speaker?.imageUrl ?? undefined
  const thumb = image ? personaThumbUrl(image, 128) : undefined
  const src = thumbFailed || !thumb ? image : thumb
  const dims = size === 'xl' ? 'h-20 w-20' : 'h-9 w-9'
  if (src && !broken) {
    return (
      <img
        src={src}
        alt={name}
        onError={() => {
          if (!thumbFailed && thumb && thumb !== image) {
            setThumbFailed(true)
            return
          }
          setBroken(true)
        }}
        className={cn(dims, 'shrink-0 rounded-full border border-twin-chat-divider object-cover')}
        data-testid="qs-quote-gallery-portrait"
      />
    )
  }
  return <UserAvatar initials={quote.speaker?.initials || name.charAt(0).toUpperCase()} name={name} size={size} />
}

export interface OpenEndQuotesPlotProps {
  plot: QuotePlot
  /** The answers the host shows (a segment cut passes its slice): quotes are kept by `respondentId`. */
  responses?: Array<{ persona_id?: string }>
  personas?: Array<QuickStudyPersona>
  populationId?: string
  comparisonLabels?: Array<string>
  documentMode?: boolean
  /** The slice matched nobody (a segment cut), not "none captured". */
  filteredEmpty?: boolean
}

/**
 * The names the host's persona list carries, by `respondentId`. Display only, like the portrait:
 * a speaker Python stamped without a name takes this one. Never invented.
 */
export function personaNamesById(personas: Array<QuickStudyPersona> | undefined): Map<string, string> {
  const names = new Map<string, string>()
  for (const persona of personas ?? []) {
    const name = (persona.resolvedIdentity?.display_name || persona.personaName || '').trim()
    if (persona.personaId && name) {
      names.set(persona.personaId, name)
    }
  }
  return names
}

/** `quote` with its speaker's missing name filled from `names` (the same quote when nothing changes). */
export function withSpeakerName(quote: QuoteListQuote, names: Map<string, string>): QuoteListQuote {
  const id = quote.speaker?.respondentId
  const name = id && !quote.speaker?.name ? names.get(id) : undefined
  return name && quote.speaker ? { ...quote, speaker: { ...quote.speaker, name } } : quote
}

/** The quote gallery (Q16): Python's join, chips and featured; the host fills missing portraits. */
export function OpenEndQuotesPlot(props: OpenEndQuotesPlotProps) {
  const { plot, responses, personas, populationId, comparisonLabels = [], documentMode = false, filteredEmpty = false } = props

  const { t } = useTranslation('app')

  const data = useMemo(() => {
    const ids = new Set((responses ?? []).map((response) => response.persona_id).filter(Boolean))
    const sliced = responses !== undefined && plot.data.quotes.some((quote) => quote.speaker?.respondentId && !ids.has(quote.speaker.respondentId))
    // A segment cut: the stamped quotes of the twins in the slice, as stamped.
    const quotes = sliced
      ? plot.data.quotes.filter((quote) => quote.speaker?.respondentId && ids.has(quote.speaker.respondentId))
      : plot.data.quotes
    // Display only, like the portrait: a speaker Python stamped without a name takes the
    // name the host's persona list carries for that `respondentId` (`withSpeakerName`).
    const names = personaNamesById(personas)
    const named = quotes.map((quote) => withSpeakerName(quote, names))
    if (!sliced && named.every((quote, index) => quote === plot.data.quotes[index])) {
      return plot.data
    }
    return { ...plot.data, quotes: named }
  }, [plot, responses, personas])

  const personaImages = useMemo(() => {
    const map: Record<string, string> = {}
    for (const persona of personas ?? []) {
      if (persona.personaId && persona.imageUrl) {
        map[persona.personaId] = persona.imageUrl
      }
    }
    return map
  }, [personas])

  const missing = useMemo(
    () =>
      data.quotes
        .filter((quote) => !quote.speaker?.imageUrl && quote.speaker?.respondentId && !personaImages[quote.speaker.respondentId])
        .map((quote) => ({ personaId: quote.speaker?.respondentId as string, name: quote.speaker?.name ?? '' })),
    [data, personaImages],
  )
  const missingKey = useMemo(() => missing.map((entry) => entry.personaId).sort().join(','), [missing])

  const [fetched, setFetched] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!populationId || missing.length === 0) {
      setFetched({})
      return
    }
    let cancelled = false
    void resolveMosaicPickerImages(populationId, missing).then((next) => {
      if (!cancelled) {
        setFetched(next)
      }
    })
    return () => {
      cancelled = true
    }
    // Keyed on the missing ids, not the list's identity (the legacy gallery's same guard).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [populationId, missingKey])

  const imageOf = (respondentId: string) => personaImages[respondentId] ?? fetched[respondentId] ?? null
  const renderText = (text: string) => <ComparisonLabelText text={text} labels={comparisonLabels} />
  const emptyLabel = filteredEmpty ? t('quickStudyChrome.quotesFilteredEmpty') : t('quickStudyChrome.quotesNoneCaptured')

  return (
    <div data-testid="qs-open-end-quote-plot">
      <QuoteGallery
        data={data}
        groupBy={plot.props?.groupBy}
        renderMode={documentMode ? 'static' : 'interactive'}
        imageOf={imageOf}
        renderAvatar={(args) => <GalleryPortrait {...args} />}
        renderText={renderText}
        emptyLabel={emptyLabel}
      />
    </div>
  )
}

/** What the narrative cites: its text, the twin (`persona_id`) and the paragraph it proves. */
export interface NarrativeQuoteRef {
  text: string
  after_paragraph: number
  persona_id?: string
  /** The version the twin saw (monadic): joined to the stamped quote's `assetId`. */
  item_id?: string
}

/**
 * The narrative's testimonials (W7-T4b, InsightCard) as the stamped gallery's cards, by paragraph:
 * the speaker and chips are Python's join for that twin (`respondentId`), the text is the one the
 * narrative quotes. The join is the twin (`respondentId`) and, when the testimonial names the
 * version it is about (`item_id`), that version (`assetId`): a twin quoted on two versions gets
 * each version's chips. A testimonial the gallery does not carry has no stamped attribution and
 * is left out, as the legacy reader drops an unattributed one.
 */
export function narrativeQuotesFromPlot(plot: QuotePlot, quotes: Array<NarrativeQuoteRef>): Map<number, Array<QuoteListQuote>> {
  const bySpeaker = new Map<string, Array<QuoteListQuote>>()
  for (const quote of plot.data.quotes) {
    const id = quote.speaker?.respondentId
    if (id) {
      bySpeaker.set(id, [...(bySpeaker.get(id) ?? []), quote])
    }
  }
  const stampedFor = (ref: NarrativeQuoteRef): QuoteListQuote | undefined => {
    const candidates = ref.persona_id ? bySpeaker.get(ref.persona_id.trim()) ?? [] : []
    const itemId = (ref.item_id ?? '').trim()
    if (!itemId || candidates.every((quote) => !quote.assetId)) {
      return candidates[0]
    }
    return candidates.find((quote) => quote.assetId === itemId)
  }
  const map = new Map<number, Array<QuoteListQuote>>()
  quotes.forEach((quote, index) => {
    const stamped = stampedFor(quote)
    if (!stamped || !quote.text.trim()) {
      return
    }
    const paragraph = quote.after_paragraph ?? 0
    const bucket = map.get(paragraph) ?? []
    bucket.push({ ...stamped, id: `narrative-${index}`, text: quote.text })
    map.set(paragraph, bucket)
  })
  return map
}

export interface NarrativeQuotePlotCardProps {
  plot: QuotePlot
  quote: QuoteListQuote
  personas?: Array<QuickStudyPersona>
  comparisonLabels?: Array<string>
}

/** One narrative testimonial drawn as the shared gallery card (portrait filled from the run's personas). */
export function NarrativeQuotePlotCard(props: NarrativeQuotePlotCardProps) {
  const { plot, quote, personas, comparisonLabels = [] } = props

  const named = useMemo(() => withSpeakerName(quote, personaNamesById(personas)), [quote, personas])
  const imageOf = (respondentId: string) =>
    (personas ?? []).find((persona) => persona.personaId === respondentId)?.imageUrl ?? null

  return (
    <div data-testid="qs-narrative-quote-plot">
      <QuoteGalleryCard
        quote={named}
        data={plot.data}
        imageOf={imageOf}
        renderAvatar={(args) => <GalleryPortrait {...args} />}
        renderText={(text) => <ComparisonLabelText text={text} labels={comparisonLabels} />}
      />
    </div>
  )
}
